"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { SubscriptionFeatureUsage } from "@/services/reportsService";

interface StaffInfo {
  id: string;
  name: string;
}

interface SubscriptionUsageDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    customers: Array<{
      customerId: string;
      customerName: string;
      byStaff: { [staffName: string]: { outside: number; inside: number } };
      totalOutside: number;
      totalInside: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: { outside: number; inside: number } };
      totalOutside: number;
      totalInside: number;
    };
    featureUsage: SubscriptionFeatureUsage[];
  };
}

const COLORS = [
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#6366f1",
  "#ec4899",
  "#14b8a6",
];

export function SubscriptionUsageDashboard({
  data,
}: SubscriptionUsageDashboardProps) {
  const staffNames = data.staff.map((s) => s.name);

  // One row per customer subscription and feature
  const featureUsageRows = data.featureUsage.flatMap((sub) =>
    sub.features.map((feature) => ({
      key: `${sub.userSubscriptionId}-${feature.featureId}`,
      customerName: sub.customerName,
      planName: sub.planName,
      featureName: feature.featureName,
      allowance: feature.allowance === null ? "Included" : feature.allowance,
      used: feature.used,
      remaining: feature.remaining === null ? "-" : feature.remaining,
    })),
  );

  const handleExportFeatureUsage = () => {
    const exportData = featureUsageRows.map((row) => ({
      "Customer Name": row.customerName,
      Plan: row.planName,
      Feature: row.featureName,
      Allowance: row.allowance,
      Used: row.used,
      Remaining: row.remaining,
    }));
    exportToExcel(exportData, "Subscription_Feature_Usage", "Feature Usage");
  };

  const handleExport = () => {
    const exportData = data.customers.map((row) => {
      const rowData: Record<string, string | number> = {
        "Customer Name": row.customerName,
      };
      staffNames.forEach((name) => {
        const staffData = row.byStaff[name] || { outside: 0, inside: 0 };
        rowData[name] = staffData.outside + staffData.inside;
      });
      rowData["Total Washes"] = row.totalOutside + row.totalInside;
      return rowData;
    });

    // Add totals row
    const totalsRow: Record<string, string | number> = {
      "Customer Name": "TOTAL",
    };
    staffNames.forEach((name) => {
      const staffData = data.totals.byStaff[name] || { outside: 0, inside: 0 };
      totalsRow[name] = staffData.outside + staffData.inside;
    });
    totalsRow["Total Washes"] =
      data.totals.totalOutside + data.totals.totalInside;
    exportData.push(totalsRow);

    exportToExcel(exportData, "Subscription_Usage", "Usage");
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Washes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.totals.totalOutside + data.totals.totalInside}
            </div>
            <p className="text-xs text-muted-foreground">All staff combined</p>
          </CardContent>
        </Card>

        {staffNames.map((name, index) => {
          const staffData = data.totals.byStaff[name] || {
            outside: 0,
            inside: 0,
          };
          return (
            <Card key={name}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {name} Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-2xl font-bold"
                  style={{ color: COLORS[index % COLORS.length] }}
                >
                  {staffData.outside + staffData.inside}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Subscription Usage by Customer</CardTitle>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  {staffNames.map((name) => (
                    <TableHead key={name} className="text-center border-l">
                      {name}
                    </TableHead>
                  ))}
                  <TableHead className="text-center border-l">
                    Total Washes
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.customers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={staffNames.length + 2}
                      className="text-center py-8"
                    >
                      No subscription usage data found
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data.customers.map((customer) => (
                      <TableRow key={customer.customerId}>
                        <TableCell className="font-medium">
                          {customer.customerName}
                        </TableCell>
                        {staffNames.map((name) => {
                          const staffData = customer.byStaff[name] || {
                            outside: 0,
                            inside: 0,
                          };
                          return (
                            <TableCell
                              key={`customer-${customer.customerId}-${name}`}
                              className="text-center border-l"
                            >
                              {staffData.outside + staffData.inside}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center border-l font-medium">
                          {customer.totalOutside + customer.totalInside}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell>TOTAL</TableCell>
                      {staffNames.map((name) => {
                        const staffData = data.totals.byStaff[name] || {
                          outside: 0,
                          inside: 0,
                        };
                        return (
                          <TableCell
                            key={`total-${name}`}
                            className="text-center border-l"
                          >
                            {staffData.outside + staffData.inside}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center border-l">
                        {data.totals.totalOutside + data.totals.totalInside}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Feature Usage by Customer */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Feature Usage by Customer</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportFeatureUsage}
          >
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">
            Used counts appointments in the selected period; Remaining is the
            current balance.
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead className="text-center">Allowance</TableHead>
                  <TableHead className="text-center">Used</TableHead>
                  <TableHead className="text-center">Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {featureUsageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      No subscription feature data found
                    </TableCell>
                  </TableRow>
                ) : (
                  featureUsageRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium">
                        {row.customerName}
                      </TableCell>
                      <TableCell>{row.planName}</TableCell>
                      <TableCell>{row.featureName}</TableCell>
                      <TableCell className="text-center">
                        {row.allowance}
                      </TableCell>
                      <TableCell className="text-center">{row.used}</TableCell>
                      <TableCell className="text-center">
                        {row.remaining}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
